/**
 * StorageService — R2-only storage facade.
 */
import { createStorageProvider } from "@/services/storage/createStorageProvider";
import type { StorageProvider, UploadOptions, UploadResult } from "@/services/storage/types";
import {
  type CanonicalStorageMetadata,
  type StorageProviderKey,
} from "@/services/storage/migrationConfig";

const getProvider = (): StorageProvider => createStorageProvider();

export type { UploadResult };

export const storageService = {
  async uploadWithMetadata(
    filePath: string,
    file: File | Blob,
    options?: UploadOptions
  ): Promise<UploadResult & { metadata: CanonicalStorageMetadata }> {
    const provider = getProvider();
    const result = await provider.upload(filePath, file, options);

    return {
      ...result,
      metadata: {
        provider: "r2",
        key: filePath,
        urlMode: "public",
      },
    };
  },

  async upload(filePath: string, file: File | Blob, options?: UploadOptions): Promise<UploadResult> {
    const { metadata: _metadata, ...result } = await this.uploadWithMetadata(filePath, file, options);
    return result;
  },

  getPublicUrl(filePath: string): string {
    return getProvider().getPublicUrl(filePath);
  },

  async resolvePublicUrl(metadata: CanonicalStorageMetadata): Promise<string> {
    return getProvider().getPublicUrl(metadata.key);
  },

  async getSignedUrl(filePath: string, expiresInSeconds?: number): Promise<string> {
    const provider = getProvider();
    if (!provider.getSignedUrl) {
      throw new Error("Storage provider does not support signed URLs.");
    }

    return provider.getSignedUrl(filePath, expiresInSeconds);
  },

  remove(filePaths: string[]): Promise<void> {
    return getProvider().remove(filePaths);
  },
};
